// Export utility functions for CSV and Excel formats

export interface ExportColumn<T = any> {
  key: keyof T;
  label: string;
  format?: (value: any) => any;
}

/**
 * Format a date for export
 */
export function formatDateForExport(date: Date | string | null | undefined): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format currency for export
 */
export function formatCurrencyForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format number for export
 */
export function formatNumberForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return value.toString();
}

/**
 * Format percentage for export
 */
export function formatPercentageForExport(value: number | null | undefined): string {
  if (value === null || value === undefined) return "";
  return `${value.toFixed(2)}%`;
}

/**
 * Escape CSV field value
 */
function escapeCSVField(value: any): string {
  if (value === null || value === undefined) return "";
  
  const stringValue = String(value);
  
  // If the value contains comma, newline, or double quote, wrap it in quotes
  if (stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')) {
    // Escape double quotes by doubling them
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
}

/**
 * Export data to CSV format
 */
export function exportToCSV<T extends Record<string, any>>(options: {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
}): void {
  const { data, columns, filename } = options;
  
  try {
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error("Data must be an array");
    }
    if (!columns || !Array.isArray(columns)) {
      throw new Error("Columns must be an array");
    }
    if (!filename) {
      throw new Error("Filename is required");
    }
    
    // Create CSV header
    const headers = columns.map(col => escapeCSVField(col.label)).join(",");
    
    // Create CSV rows
    const rows = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        const formattedValue = col.format ? col.format(value) : value;
        return escapeCSVField(formattedValue);
      }).join(",")
    );
    
    // Combine header and rows
    const csv = [headers, ...rows].join("\n");
    
    // Create blob and download
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    downloadFile(blob, filename);
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    throw error;
  }
}

/**
 * Export data to Excel format (XLSX)
 * This creates a simple XML-based Excel file without external dependencies
 */
export function exportToExcel<T extends Record<string, any>>(options: {
  data: T[];
  columns: ExportColumn<T>[];
  filename: string;
  sheetName?: string;
}): void {
  const { data, columns, filename, sheetName = "Sheet1" } = options;
  
  try {
    // Validate inputs
    if (!data || !Array.isArray(data)) {
      throw new Error("Data must be an array");
    }
    if (!columns || !Array.isArray(columns)) {
      throw new Error("Columns must be an array");
    }
    if (!filename) {
      throw new Error("Filename is required");
    }
    
    // Create Excel XML content
    const xmlHeader = '<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>';
    const workbookStart = '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">';
    const workbookEnd = '</Workbook>';
    
    // Create worksheet
    let worksheet = `<Worksheet ss:Name="${escapeXML(sheetName)}"><Table>`;
    
    // Add header row
    worksheet += '<Row>';
    columns.forEach(col => {
      worksheet += `<Cell><Data ss:Type="String">${escapeXML(col.label)}</Data></Cell>`;
    });
    worksheet += '</Row>';
    
    // Add data rows
    data.forEach(row => {
      worksheet += '<Row>';
      columns.forEach(col => {
        const value = row[col.key];
        const formattedValue = col.format ? col.format(value) : value;
        const { type, content } = getCellTypeAndContent(formattedValue);
        worksheet += `<Cell><Data ss:Type="${type}">${escapeXML(content)}</Data></Cell>`;
      });
      worksheet += '</Row>';
    });
    
    worksheet += '</Table></Worksheet>';
    
    // Combine all parts
    const excelContent = xmlHeader + workbookStart + worksheet + workbookEnd;
    
    // Create blob and download
    const blob = new Blob([excelContent], { 
      type: "application/vnd.ms-excel;charset=utf-8;" 
    });
    downloadFile(blob, filename);
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    throw error;
  }
}

/**
 * Escape XML special characters
 */
function escapeXML(value: any): string {
  if (value === null || value === undefined) return "";
  
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/**
 * Determine cell type and content for Excel
 */
function getCellTypeAndContent(value: any): { type: string; content: string } {
  if (value === null || value === undefined) {
    return { type: "String", content: "" };
  }
  
  if (typeof value === "number") {
    return { type: "Number", content: String(value) };
  }
  
  if (typeof value === "boolean") {
    return { type: "Boolean", content: value ? "1" : "0" };
  }
  
  if (value instanceof Date) {
    return { type: "String", content: formatDateForExport(value) };
  }
  
  return { type: "String", content: String(value) };
}

/**
 * Download a file
 */
function downloadFile(blob: Blob, filename: string): void {
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Generate a filename with timestamp
 */
export function generateFilename(prefix: string, extension: "csv" | "xls"): string {
  const now = new Date();
  const timestamp = now.toISOString().replace(/[:.]/g, "-").slice(0, -5);
  return `${prefix}_${timestamp}.${extension}`;
}
